import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { Temporal } from 'temporal-polyfill';
import { Linkifier } from '../linkifier.js';
import { linkStyles } from './cssstyles.js';

export type DayStatus = WireTypes.SpanOutcome|'loading';

export type DayData = {
  date: Temporal.PlainDate,
  status: DayStatus,
}

type CalendarDay = {
  dayOfMonth: number, // 1-31
  dayOfWeek: number, // 0-6
  relativeWeek: number, // 0-.. US weeks in the selected interval, starting with the 0 as the first week in the interval.
  date: Temporal.PlainDate,
  status: DayStatus,
}

export type CalendarViewProvider = (days: CalendarDay[], signal: AbortSignal) => Promise<DayStatus[]>;

function formatFullDate(date: Temporal.PlainDate) {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatDateRange(since: Temporal.PlainDate, until: Temporal.PlainDate) {
  const sinceText = since.year === until.year ? since.toLocaleString('en-US', { month: 'short', day: 'numeric' }) : formatFullDate(since);
  return `${sinceText} – ${formatFullDate(until)}`;
}

function formatMonthOnly(date: Temporal.PlainDate) {
  return date.toLocaleString('en-US', { month: 'short' });
}

// This should render a calendar month with days highlighted with outcomes.
// When a day is clicked, an event should be emitted.
@customElement('github-calendar')
class GithubCalendar extends LitElement {
  @property({ attribute: false }) selectedSince?: Temporal.PlainDate;
  @property({ attribute: false }) selectedUntil?: Temporal.PlainDate;
  @property({ attribute: false }) data?: DayData[];
  @property({ attribute: false }) linkifier?: Linkifier<Temporal.PlainDate>;
  // Today in the project's timezone; lets the caption call out today and yesterday.
  @property({ attribute: false }) today?: Temporal.PlainDate;

  @state() private _hoveredDay?: Temporal.PlainDate;

  private _onMouseEnter(since: Temporal.PlainDate, e: MouseEvent) {
    this._hoveredDay = since;
  }

  private _onMouseLeave() {
    this._hoveredDay = undefined;
  }

  private _formatDay(date: Temporal.PlainDate) {
    const text = formatFullDate(date);
    if (this.today && date.equals(this.today))
      return `Today · ${text}`;
    if (this.today && date.equals(this.today.subtract({ days: 1 })))
      return `Yesterday · ${text}`;
    return text;
  }

  private _isSelected(day: CalendarDay) {
    if (!this.selectedSince || !this.selectedUntil)
      return false;
    return Temporal.PlainDate.compare(this.selectedSince, day.date) <= 0 && Temporal.PlainDate.compare(day.date, this.selectedUntil) <= 0;
  }

  render() {
    if (!this.data || !this.data.length)
      return nothing;
    let minDate = this.data[0].date;
    let maxDate = this.data[0].date;
    for (const d of this.data) {
      if (Temporal.PlainDate.compare(d.date, minDate) < 0)
        minDate = d.date;
      if (Temporal.PlainDate.compare(d.date, maxDate) > 0)
        maxDate = d.date;
    }

    const dayToStatus = new Map(this.data.map(({ date, status }) => [date.toString(), status]));
    let weekCounter = 0;
    const days: CalendarDay[] = [];
    for (let d = minDate; Temporal.PlainDate.compare(d, maxDate) <= 0; d = d.add({ days: 1 })) {
      days.push({
        dayOfMonth: d.day,
        dayOfWeek: d.dayOfWeek % 7,
        relativeWeek: d.dayOfWeek === 6 ? weekCounter++ : weekCounter,
        date: d,
        status: dayToStatus.get(d.toString()) ?? 'idle',
      });
    }

    const sundays = days.filter(day => day.dayOfWeek === 0);
    const allMonthLabels = sundays.filter((day, index) => index === 0 || sundays[index].dayOfMonth < sundays[index - 1].dayOfMonth);
    const monthLabels = allMonthLabels.filter((day, index) => {
      // We always take the last month.
      if (index === allMonthLabels.length - 1)
        return true;
      const next = allMonthLabels[index + 1];
      return next.relativeWeek - day.relativeWeek > 1;
    });
    const lastWeek = Math.max(...days.map(day => day.relativeWeek));
    // The top row names the hovered day, or else the selection, so the selected date
    // can be read at a glance. Month labels live in their own row under the days.
    const selectionCaption = !this.selectedSince || !this.selectedUntil ? undefined
      : this.selectedSince.equals(this.selectedUntil) ? this._formatDay(this.selectedSince)
      : formatDateRange(this.selectedSince, this.selectedUntil);
    const caption = this._hoveredDay ? this._formatDay(this._hoveredDay) : selectionCaption;
    // A hovered day is only a preview, so it is muted to tell it apart from the selection.
    const isHoverCaption = this._hoveredDay !== undefined;
    return html`
      <section class=days style=${styleMap({
        '--weeks': lastWeek + 1,
      })}>
        <h-box style="gap: 0; grid-row: 1; grid-column: 1;">
          <sl-icon-button name=chevron-left @click=${() => {
            this.dispatchEvent(new CustomEvent('fk-shift-left'));
          }}></sl-icon-button>
        </h-box>
        <sl-icon-button name=chevron-right style="grid-row: 1; grid-column: ${lastWeek + 3};"  @click=${() => {
          this.dispatchEvent(new CustomEvent('fk-shift-right'));
        }}></sl-icon-button>
        <span class=day-of-week style="grid-row: 3; grid-column: 1;">Mon</span>
        <span class=day-of-week style="grid-row: 5; grid-column: 1;">Wed</span>
        <span class=day-of-week style="grid-row: 7; grid-column: 1;">Fri</span>

        ${caption ? html`
          <span class=caption style=${styleMap({
            'grid-column-start': 2,
            'grid-column-end': lastWeek + 3,
            'align-self': 'center',
            'justify-self': 'center',
            'font-size': 'var(--sl-font-size-medium)',
            'font-weight': isHoverCaption ? 'var(--sl-font-weight-normal)' : 'var(--sl-font-weight-semibold)',
            'color': isHoverCaption ? 'var(--sl-color-neutral-500)' : undefined,
            'width': 'auto',
            gridRow: 1,
          })}>${caption}</span>
        ` : nothing}
        ${monthLabels.map(({ relativeWeek, date }) => html`
          <span class=month-of-year style=${styleMap({
            gridColumn: relativeWeek + 2,
            gridRow: 9,
          })}>${formatMonthOnly(date)}</span>
        `)}
        <div style=${styleMap({
          'grid-column': '-2',
          'grid-row': '2/9',
        })}>
          <slot name=widgets></slot>
        </div>
        <section style=${styleMap({
          'display': 'grid',
          'grid-template-rows': 'subgrid',
          'grid-template-columns': 'subgrid',
          'grid-column': '2/-2',
          'grid-row': '2/9',
        })} @mouseleave=${this._onMouseLeave}>
          ${days.map(day => html`
            <span
              class=day-container
              ?selected=${this._isSelected(day)}
              style=${styleMap({
                gridColumn: day.relativeWeek + 1,
                gridRow: day.dayOfWeek + 1,
              })}
            >
              <day-outcome
                size=large
                ?selected=${this._isSelected(day)}
                .outcome=${day.status === 'loading' ? undefined : day.status}
                .href=${this.linkifier?.(day.date)}
                @mouseenter=${this._onMouseEnter.bind(this, day.date)}
              ></day-outcome>
            </span>
        `)}
        </section>
      </section>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      display: flex;
      align-items: center;
    }

    .days {
      --size: 14px;
      display: grid;
      grid-template-columns: auto repeat(var(--weeks), auto) auto;
      /* The caption, a row per day of the week, then the month labels. */
      grid-template-rows: auto repeat(7, auto) auto;
      gap: 0;
    }

    .controls {
      font-size: var(--sl-font-size-small);
    }

    .day-of-week {
      font-size: var(--sl-font-size-small);
      display: flex;
      align-items: center;
      height: var(--size);
    }

    .month-of-year {
      font-size: var(--sl-font-size-small);
      width: var(--size);
      color: var(--sl-color-neutral-500);
      padding-top: var(--sl-spacing-2x-small);
    }

    .days:has(.day.selected) .day:not(.selected) {
    }

    .day-container {
      width: 19px;
      height: 19px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--sl-border-radius-small);
      box-sizing: border-box;
      &[selected] {
        
      }
    }

    day-outcome {
      cursor: pointer;

      &[selected] {
        border-radius: 2px;
      }
    }

    day-outcome:hover {
      filter: brightness(0.8);
    }
  `];
}